import { Chip, IconButton } from "@mui/material";
import type { CSSProperties } from "react";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Cell, Pie, PieChart, Sector, type PieLabelRenderProps } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "../../components/ui/chart";
import { AppliedFilterChips, type AppliedFilterChip } from "../../components/AppliedFilterChips";
import { getCategoryIconComponent } from "../../lib/categoryIcons";
import type { Category, ReportCategoryStat } from "../../lib/types";
import { formatCurrency } from "./formatters";
import { categoryPalette } from "./StatisticsRankings";
import { currentStatisticsMonth, shiftStatisticsMonth, statisticsMonthLabel } from "./dateRange";

export function expenseCategoryKey(item: Pick<ReportCategoryStat, "category_id">) {
  return item.category_id || "uncategorized";
}

function isNearWhiteColor(color?: string | null) {
  const match = color?.trim().match(/^#([0-9a-f]{6})$/i);
  if (!match) return false;
  const value = Number.parseInt(match[1], 16);
  const red = (value >> 16) & 255;
  const green = (value >> 8) & 255;
  const blue = value & 255;
  const brightness = (red * 299 + green * 587 + blue * 114) / 255000;
  return brightness >= 0.9;
}

export function collapsedOtherCategoryItems(items: ReportCategoryStat[]) {
  const total = items.reduce((sum, item) => sum + Number(item.amount), 0);
  if (!total) return [];
  const smallItems = items.filter((item) => Number(item.amount) / total * 100 <= 5);
  return smallItems.length >= 2 ? smallItems : [];
}

export type MonthlyAccountFilter = {
  id: string;
  name: string;
  currency: string;
  amount: number;
  icon?: string | null;
  color?: string | null;
  selected: boolean;
};

type MonthlyExpenseBreakdownProps = {
  items: ReportCategoryStat[];
  categories: Category[];
  total: string;
  currency: string;
  selectedParentCategory: string;
  selectedChildCategories: string[];
  otherActive: boolean;
  onSelectParentCategory: (category: string) => void;
  onToggleChildCategory: (category: string) => void;
  onSelectOther: () => void;
  month: string;
  onMonthChange: (month: string) => void;
  today?: Date;
  filterChips: AppliedFilterChip[];
  onDeleteFilter: (key: string) => void;
  totalLabel?: string;
  categoryInteraction?: boolean;
  onSelectBreakdownItem?: (key: string) => void;
  accountFilters?: MonthlyAccountFilter[];
  onToggleAccount?: (accountId: string) => void;
};

export function aggregateParentCategories(items: ReportCategoryStat[], categories: Category[]) {
  const categoryById = new Map(categories.map((category) => [category.id, category]));
  const aggregated = new Map<string, ReportCategoryStat>();
  for (const item of items) {
    const category = item.category_id ? categoryById.get(item.category_id) : undefined;
    const parent = category?.parent_id ? categoryById.get(category.parent_id) : category;
    const key = parent?.id || expenseCategoryKey(item);
    const current = aggregated.get(key);
    if (!current) {
      aggregated.set(key, {
        ...item,
        category_id: parent?.id || item.category_id,
        category_name: parent?.name || item.category_name,
        icon: parent?.icon || item.icon,
        color: parent?.color || item.color,
      });
      continue;
    }
    const amount = Number(current.amount) + Number(item.amount);
    const count = current.count + item.count;
    const previousAmount = Number(current.previous_amount) + Number(item.previous_amount);
    current.amount = String(amount);
    current.count = count;
    current.average_amount = String(count ? amount / count : 0);
    current.percent += item.percent;
    current.previous_amount = String(previousAmount);
    current.amount_delta = String(amount - previousAmount);
  }
  return [...aggregated.values()].sort((a, b) => Number(b.amount) - Number(a.amount));
}

function CategorySectorLabel({ cx, cy, startAngle, midAngle, endAngle, innerRadius, outerRadius, payload }: PieLabelRenderProps) {
  const share = Number((payload as { share?: number } | undefined)?.share || 0);
  if (share < 0.015) return null;
  const centerX = Number(cx || 0);
  const centerY = Number(cy || 0);
  const middleAngle = -Number(midAngle || 0) * Math.PI / 180;
  const outer = Number(outerRadius || 0);
  const percentageRadius = outer + 18;
  const percentageX = centerX + percentageRadius * Math.cos(middleAngle);
  const percentageY = centerY + percentageRadius * Math.sin(middleAngle);
  const showIcon = share >= 0.02;
  const item = payload as { icon?: string | null; color?: string | null } | undefined;
  const Icon = getCategoryIconComponent(item?.icon);
  const nearWhite = isNearWhiteColor(item?.color);
  const inner = Number(innerRadius || 0);
  const iconRadius = (inner + outer) / 2;
  const sectorSpan = Math.abs(Number(startAngle || 0) - Number(endAngle || 0));
  const markerSpan = Math.min(sectorSpan * 0.72, iconRadius ? 32 / iconRadius * 180 / Math.PI : 0);
  const sectorStart = Number(startAngle ?? midAngle ?? 0);
  const directionToMiddle = Math.sign(Number(midAngle || 0) - sectorStart) || 1;
  const markerInnerEdge = sectorStart + directionToMiddle * markerSpan;
  const iconAngle = -(sectorStart + directionToMiddle * markerSpan / 2) * Math.PI / 180;
  const iconX = centerX + iconRadius * Math.cos(iconAngle);
  const iconY = centerY + iconRadius * Math.sin(iconAngle);
  return (
    <g className="monthly-expense-sector-label">
      <text x={percentageX} y={percentageY}>{Math.round(share * 100)}%</text>
      {showIcon ? <>
        <Sector cx={centerX} cy={centerY} innerRadius={inner} outerRadius={outer} startAngle={sectorStart} endAngle={markerInnerEdge} cornerRadius={6} fill={nearWhite ? "#f8fafc" : "rgba(255, 255, 255, 0.24)"} stroke={nearWhite ? "#94a3b8" : "rgba(255, 255, 255, 0.82)"} strokeWidth={1} className="monthly-expense-sector-marker" />
        <foreignObject x={iconX - 9} y={iconY - 9} width={18} height={18} className="monthly-expense-sector-icon">
          <div><Icon aria-hidden="true" /></div>
        </foreignObject>
      </> : null}
    </g>
  );
}

export function MonthlyExpenseBreakdown({ items, categories, total, currency, selectedParentCategory, selectedChildCategories, otherActive, onSelectParentCategory, onToggleChildCategory, onSelectOther, month, onMonthChange, today, filterChips, onDeleteFilter, totalLabel = "Expenses", categoryInteraction = true, onSelectBreakdownItem, accountFilters, onToggleAccount }: MonthlyExpenseBreakdownProps) {
  const categoryById = new Map(categories.map((category) => [category.id, category]));
  const parentItems = aggregateParentCategories(items, categories);
  const activeParentId = selectedParentCategory !== "uncategorized" ? selectedParentCategory : "";
  const childItems = activeParentId
    ? items.filter((item) => item.category_id && categoryById.get(item.category_id)?.parent_id === activeParentId)
    : [];
  const directParentItems = activeParentId ? items.filter((item) => item.category_id === activeParentId) : [];
  const parentScopeItems = activeParentId ? [...directParentItems, ...childItems] : [];
  const navigationSource = activeParentId ? childItems : parentItems;
  const otherItems = collapsedOtherCategoryItems(navigationSource);
  const otherItemKeys = new Set(otherItems.map(expenseCategoryKey));
  const unselectedOtherItems = otherItems.filter((item) => !selectedChildCategories.includes(expenseCategoryKey(item)));
  const selectedItems = activeParentId
    ? items.filter((item) => item.category_id && selectedChildCategories.includes(item.category_id))
    : parentItems.filter((item) => item.category_id && selectedChildCategories.includes(item.category_id));
  const chartItems = selectedParentCategory === "uncategorized"
    ? items.filter((item) => expenseCategoryKey(item) === "uncategorized")
    : selectedChildCategories.length
      ? selectedItems
      : otherActive
        ? otherItems
        : activeParentId
          ? parentScopeItems
          : parentItems;
  const filteredTotal = chartItems.reduce((sum, item) => sum + Number(item.amount), 0);
  const collapseSmallCategories = categoryInteraction && !otherActive && !selectedChildCategories.length && selectedParentCategory !== "uncategorized";
  const prominentChartItems = collapseSmallCategories ? chartItems.filter((item) => !otherItemKeys.has(expenseCategoryKey(item)) || item.category_id === activeParentId) : chartItems;
  const smallChartItems = collapseSmallCategories ? chartItems.filter((item) => otherItemKeys.has(expenseCategoryKey(item)) && item.category_id !== activeParentId) : [];
  const prominentNavigationItems = categoryInteraction ? navigationSource.filter((item) => !otherItemKeys.has(expenseCategoryKey(item)) || selectedChildCategories.includes(expenseCategoryKey(item))) : navigationSource;
  const navigationItems = otherActive ? otherItems : prominentNavigationItems;
  const restAmount = smallChartItems.reduce((sum, item) => sum + Number(item.amount), 0);
  const restPercent = filteredTotal ? restAmount / filteredTotal * 100 : 0;
  const visibleItems = prominentChartItems;
  const data = visibleItems.map((item, index) => ({ ...item, key: expenseCategoryKey(item), name: item.category_name, value: Number(item.amount), share: filteredTotal ? Number(item.amount) / filteredTotal : 0, color: item.color || categoryPalette[index % categoryPalette.length], other: false }));
  if (restAmount > 0) data.push({ category_id: null, category_name: "Other", amount: String(restAmount), average_amount: "0", count: 0, previous_amount: "0", amount_delta: "0", percent: restPercent, key: "other", name: "Other", value: restAmount, share: filteredTotal ? restAmount / filteredTotal : 0, color: "#94a3b8", other: true });
  const chartData = data.filter((item) => item.value > 0);
  const config = Object.fromEntries(chartData.map((item) => [item.key, { label: item.name, color: item.color }])) as ChartConfig;
  const hasCategorySelection = Boolean(selectedParentCategory || selectedChildCategories.length || otherActive);
  const displayedTotal = hasCategorySelection ? filteredTotal : Number(total);
  const displayedTotalText = formatCurrency(displayedTotal, currency);
  const totalFontScale = Math.max(0.62, Math.min(1, 9 / displayedTotalText.length));
  const currentMonth = currentStatisticsMonth(today);
  const isCurrentMonth = month === currentMonth;
  const OtherIcon = getCategoryIconComponent("dots");

  const categoryChip = (item: ReportCategoryStat) => {
    const key = expenseCategoryKey(item);
    const selected = (activeParentId || otherActive) ? selectedChildCategories.includes(key) : selectedParentCategory === key;
    const color = item.color || "#64748b";
    const nearWhite = isNearWhiteColor(color);
    const Icon = getCategoryIconComponent(item.icon);
    const onClick = onSelectBreakdownItem ? () => onSelectBreakdownItem(key) : categoryInteraction ? () => (activeParentId || otherActive) ? onToggleChildCategory(key) : onSelectParentCategory(key) : undefined;
    return <Chip key={key} icon={<Icon aria-hidden="true" />} label={`${item.category_name} · ${formatCurrency(item.amount, currency)}`} size="small" clickable={Boolean(onClick)} variant="outlined" className={selected ? "selected" : ""} style={{ backgroundColor: nearWhite ? (selected ? "#e2e8f0" : "#f8fafc") : `${color}${selected ? "38" : "18"}`, borderColor: nearWhite ? "#94a3b8" : color, color: "#0f172a" }} onClick={onClick} />;
  };

  return (
    <section className="monthly-expense-breakdown" aria-labelledby="monthlyExpenseTitle">
      <div className="monthly-expense-heading">
        <h2 id="monthlyExpenseTitle">Transactions · {statisticsMonthLabel(month)}</h2>
      </div>
      <AppliedFilterChips chips={filterChips} className="monthly-expense-filter-chips" ariaLabel="Active statistics filters" onDelete={onDeleteFilter} />
      <>
        <div className="monthly-expense-donut-navigation">
          <IconButton type="button" aria-label="Previous month" title="Previous month" onClick={() => onMonthChange(shiftStatisticsMonth(month, -1, today))}><ArrowBackIosNewIcon fontSize="small" /></IconButton>
          <div className={`monthly-expense-donut${chartData.length ? "" : " empty"}`}>
            {chartData.length ? <ChartContainer config={config} className="stat-donut-chart">
                <PieChart accessibilityLayer margin={{ top: 14, right: 14, bottom: 14, left: 14 }}>
                  <ChartTooltip content={<ChartTooltipContent valueFormatter={(value) => formatCurrency(value, currency)} />} />
                  <Pie data={chartData} dataKey="value" nameKey="name" innerRadius="55%" outerRadius="84%" minAngle={18} cornerRadius={6} strokeWidth={2} paddingAngle={2} labelLine={false} label={CategorySectorLabel} animationBegin={0} animationDuration={280} animationEasing="ease-out">
                    {chartData.map((entry) => {
                      const onClick = onSelectBreakdownItem ? () => onSelectBreakdownItem(entry.key) : categoryInteraction ? () => entry.other ? onSelectOther() : (activeParentId || otherActive) ? onToggleChildCategory(entry.key) : onSelectParentCategory(entry.key) : undefined;
                      const nearWhite = isNearWhiteColor(entry.color);
                      return <Cell key={entry.key} fill={entry.color} stroke={nearWhite ? "#94a3b8" : "#fff"} strokeWidth={nearWhite ? 1.5 : 2} onClick={onClick} className={onClick ? "monthly-expense-sector" : undefined} />;
                    })}
                  </Pie>
                </PieChart>
              </ChartContainer> : <div className="stat-donut-chart" aria-hidden="true" />}
            <div className="monthly-expense-total" style={{ "--monthly-total-scale": totalFontScale } as CSSProperties} aria-label={`${totalLabel} ${displayedTotalText}`}><span>{totalLabel}</span><strong>{displayedTotalText}</strong></div>
          </div>
          <IconButton type="button" aria-label="Next month" title="Next month" disabled={isCurrentMonth} onClick={() => onMonthChange(shiftStatisticsMonth(month, 1, today))}><ArrowForwardIosIcon fontSize="small" /></IconButton>
        </div>
        {!accountFilters ? <div className="monthly-expense-category-chips" aria-label={otherActive ? "Categories in Other" : activeParentId ? "Subcategories" : "Parent categories"}>
          {navigationItems.map(categoryChip)}
          {categoryInteraction && !otherActive && unselectedOtherItems.length ? <Chip icon={<OtherIcon aria-hidden="true" />} label={`Other · ${formatCurrency(unselectedOtherItems.reduce((sum, item) => sum + Number(item.amount), 0), currency)}`} size="small" clickable variant="outlined" style={{ backgroundColor: "#94a3b818", borderColor: "#94a3b8", color: "#0f172a" }} onClick={onSelectOther} /> : null}
        </div> : null}
        {accountFilters ? <div className="monthly-expense-account-chips" aria-label="Account filters">
          {accountFilters.map((account) => {
            const Icon = getCategoryIconComponent(account.icon);
            return <Chip key={account.id} icon={<Icon aria-hidden="true" />} label={`${account.name} · ${formatCurrency(account.amount, account.currency)}`} size="small" clickable variant="outlined" className={account.selected ? "selected" : ""} style={{ borderColor: account.color || "#64748b" }} onClick={() => onToggleAccount?.(account.id)} />;
          })}
        </div> : null}
        {!accountFilters && activeParentId && !navigationSource.length ? <p className="monthly-expense-category-empty">No subcategories with transactions.</p> : null}
        <span className="sr-only" aria-live="polite">{filterChips.length ? `${filterChips.map((chip) => chip.label).join(", ")} selected` : "All categories selected"}</span>
      </>
    </section>
  );
}
